<template>
<q-btn-dropdown flat dense icon="settings" class="q-mx-sm">
  <q-list>
    <q-separator v-if="isMenu" />
    <slot name="before"></slot>
    <q-item
      v-if="selectedCampaign"
      clickable
      @click="showCampaignDialog = true"
    >
      <q-item-section avatar>
        <q-avatar icon="las la-info-circle"/>
      </q-item-section>
      <q-item-section>
        <q-item-label>{{ $t('tools.panorama.infoCampaign') }}</q-item-label>
      </q-item-section>
    </q-item>
    <q-separator v-if="selectedCampaign" />
    <q-item
      v-close-popup
      clickable
      :disable="!latLng"
      @click="activatePannellum"
    >
      <q-item-section avatar>
        <q-avatar icon="panorama"/>
      </q-item-section>
      <q-item-section>
        <q-item-label>{{ showPannellum ? $t('tools.panorama.disablePano') : $t('tools.panorama.enablePano') }}</q-item-label>
        <q-item-label caption>{{ $t('tools.panorama.enablePanoDescription') }}</q-item-label>
      </q-item-section>
    </q-item>
    <q-item
      clickable
      :disable="!latLng"
      v-close-popup
      @click="activatePotree"
    >
      <q-item-section avatar>
        <q-avatar icon="terrain"/>
      </q-item-section>
      <q-item-section>
        <q-item-label>{{ showPotree ? $t('tools.panorama.disable3D') : $t('tools.panorama.enable3D') }}</q-item-label>
        <q-item-label caption>{{ $t('tools.panorama.enable3DDescription') }}</q-item-label>
      </q-item-section>
    </q-item>
    <slot name="after"></slot>
    <q-separator />
    <panorama-incidence />
  </q-list>
  <q-dialog v-model="showCampaignDialog" v-if="selectedCampaign ">
    <q-card class="q-pa-sm q-pb-md" style="min-width: 400px">
      <q-card-section class="row items-center q-py-md">
        <q-icon name="las la-info-circle" style="font-size: 1.2em;" class="q-pr-sm"/>
        <div class="text-title q-pr-lg">Informació campanya activa</div>
        <q-space />
        <q-btn icon="close" flat round dense v-close-popup />
      </q-card-section>
      <q-separator />
      <q-card-section class="q-py-xs">
        <div class="text-subtitle2">Nom de la campanya</div>
        <div class="text-subtitle">{{ selectedCampaign.name }}</div>
      </q-card-section>
      <q-card-section class="q-py-xs">
        <div class="text-subtitle2">{{ $t('tools.panorama.dateCampaign') }}</div>
        <div>{{ date_start }} - {{ date_end }}</div>
      </q-card-section>
      <span v-if="selectedCampaign.metadata">
        <q-card-section class="q-py-xs" v-show="selectedCampaign.metadata.method !== null">
          <div class="text-subtitle2">Mètode de captura</div>
          <div class="text-subtitle">{{ metadataMethods[selectedCampaign.metadata.method - 1] }}</div>
        </q-card-section>
        <q-card-section class="q-py-xs" v-show="selectedCampaign.metadata.sensor">
          <div class="text-subtitle2">Sensor</div>
          <div class="text-subtitle">{{ selectedCampaign.metadata.sensor }}</div>
        </q-card-section>
        <q-card-section class="q-py-xs" v-show="selectedCampaign.metadata.precision">
          <div class="text-subtitle2">Precisió</div>
          <div class="text-subtitle">{{ selectedCampaign.metadata.precision }}</div>
        </q-card-section>
      </span>
    </q-card>
  </q-dialog>
</q-btn-dropdown>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import proj4 from 'proj4'
import { ClosePopup, QAvatar, QCard, QCardSection, QDialog, QBtnDropdown, QList, QIcon, QItem, QItemLabel, QItemSection, QSeparator } from 'quasar'

import { projections } from 'src/lib/projections'
import { calculateTargetPoint } from 'src/lib/geometry'
import PanoramaIncidence from 'components/Panorama/PanoramaIncidence.vue'

export default {
  props: ['isHeader', 'isMenu'],
  data () {
    return {
      showCampaignDialog: false
    }
  },
  components: {
    PanoramaIncidence, QAvatar, QCard, QCardSection, QDialog, QBtnDropdown, QList, QIcon, QItem, QItemLabel, QItemSection, QSeparator
  },
  directives: {
    ClosePopup
  },
  computed: {
    ...mapState({
      selectedCampaign: state => state.panorama.selectedCampaign,
      campaigns: state => state.panorama.campaigns,
      latLng: state => state.panorama.latLng,
      head: state => state.panorama.head,
      altitude: state => state.panorama.altitude,
      metadataMethods: state => state.panorama.metadataMethods,
      showPannellum: state => state.panorama.showPannellum,
      showPotree: state => state.panorama.showPotree,
      srcPannellum: state => state.panorama.srcPannellum,
      srcPotree: state => state.panorama.srcPotree
    }),
    date_start () {
      return this.selectedCampaign.date_start && moment(this.selectedCampaign.date_start).format('DD/MM/YYYY')
    },
    date_end () {
      return this.selectedCampaign.date_fi && moment(this.selectedCampaign.date_fi).format('DD/MM/YYYY')
    }
  },
  methods: {
    activatePannellum () {
      const isActive = this.$store.state.panorama.showPannellum
      this.$store.commit('panorama/setShowPannellum', !isActive)
    },
    activatePotree () {
      const isActive = this.$store.state.panorama.showPotree
      this.$store.commit('panorama/setShowPotree', !isActive)
    },
    openPano () {
      if (this.srcPannellum) {
        window.open(this.srcPannellum)
      }
    },
    openPointCloud () {
      if (this.srcPotree) {
        proj4.defs(projections)
        const epsg = this.selectedCampaign ? this.selectedCampaign.properties.epsg : this.campaigns[0].epsg
        const latLngProj = proj4('EPSG:4326', `EPSG:${epsg}`).forward([this.latLng.lng, this.latLng.lat])
        const latLngTarget = calculateTargetPoint(latLngProj, this.head, 100)
        const params = `&position=${latLngProj[0]},${latLngProj[1]},${this.altitude}&lookat=${latLngTarget[0]},${latLngTarget[1]},${this.altitude}&lydlg=true`
        window.open(this.srcPotree + params)
      }
    }
  }
}
</script>
